import { action, observable } from 'mobx';
import { RootStore } from 'stores/rootStore';
import { ApiStore } from 'stores/apiStore';
import {
  API_ACCOUNT_DETAIL_SUMMARIES,
  API_ACCOUNT_DETAIL_REPORTS,
  API_ACCOUNT_DETAIL_REPORT_REQUEST,
  API_ACCOUNT_DETAIL_DOWNLOAD_STATEMENT,
  API_ACCOUNT_DETAIL_DOWNLOAD_PERIODICAL_STATEMENT
} from 'api/endpoints';
import { sqlDateToLabel } from 'helpers/helpers';

export enum DocumentCategory {
  REPORTS = 'REPORTS',
  SUMMARIES = 'SUMMARIES',
}

export enum DocumentStatus {
  WAITING = 'WAITING',
  DONE = 'DONE',
  FAILED = 'FAILED'
}

export enum DocumentType {
  TAX = 'TAX',
  LTIP_SUMMARY = 'LTIP_SUMMARY',
  MONTH_SUMMARY = 'MONTH_SUMMARY',
  YEAR_SUMMARY = 'YEAR_SUMMARY',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export interface StatementData {
  date: string;
  type: DocumentType;
  id: string;
  request?: {
    id: string;
    pdfFileId?: string;
    pdfTemplateId?: string;
    status: DocumentStatus;
    waitingTooLong?: boolean;
  };
}

export interface Document extends StatementData {
  loading: boolean;
}

export class ReportsStore {
  rootStore: RootStore;
  accountId: string;
  apiStore: ApiStore;
  @observable data: { summaries: Document[]; reports: Document[] };
  @observable waitingTooLong = false;
  @observable copied = false;
  timerID: number;

  constructor(rootStore: RootStore, accountId: string) {
    this.rootStore = rootStore;
    this.accountId = accountId;
    this.apiStore = rootStore.stores.apiStore;
    this.data = { summaries: [], reports: [] };
    this.timerID = 0;
  }

  @action fetchReports = async () => {
    const response = await this.apiStore.get<Document[]>(API_ACCOUNT_DETAIL_REPORTS(this.accountId));
    if (response?.data) {
      this.data.reports = response?.data.map((doc: Document, i) => {
        doc.id = doc.request?.id || `${doc.type}_${doc.date}`;
        doc.loading = false;
        return doc;
      });
      if (this.data.reports.some(doc => doc.request?.waitingTooLong)) this.waitingTooLong = true;
    }
  };

  @action fetchSummaries = async () => {
    const response = await this.apiStore.get<Document[]>(API_ACCOUNT_DETAIL_SUMMARIES(this.accountId));
    if (response?.data) {
      this.data.summaries = response?.data.map((doc: Document, i) => {
        doc.id = doc.id || `${doc.type}_${doc.date}`;
        doc.loading = false;
        return doc;
      });
    }
  };

  @action copySummaries = () => {
    const copy = this.data.summaries.filter(s => s.type === 'TAX' || s.type === 'LTIP_SUMMARY');
    this.data.reports = this.data.reports.concat(...copy);
    this.copied = true;
  };

  @action checkReports = async () => {
    await this.fetchReports();
    if (this.data.reports.some((s) => s.request?.status === DocumentStatus.WAITING)) {
      this.clearTimer();
      this.timerID = window.setTimeout(this.checkReports, 5000);
    } else {
      this.clearTimer();
    }
  };

  @action fetchData = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    await this.checkReports();
    await this.fetchSummaries();
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action generateDocument = async (document: Document) => {
    this.setLoadingForDocument(document, true);
    await this.apiStore.post(API_ACCOUNT_DETAIL_REPORT_REQUEST(this.accountId), {
      date: sqlDateToLabel(document.date, 'yyyy-MM'),
      type: document.type,
    });
    this.checkReports();
  };

  @action downloadDocument = async (document: Document) => {
    this.setLoadingForDocument(document, true);
    await this.apiStore.get(API_ACCOUNT_DETAIL_DOWNLOAD_PERIODICAL_STATEMENT(this.accountId, document.request?.pdfFileId));
    this.setLoadingForDocument(document, false);
  };

  @action downloadOcpDocument = async (document: Document) => {
    this.setLoadingForOcpDocument(document, true);
    await this.apiStore.get(API_ACCOUNT_DETAIL_DOWNLOAD_STATEMENT(this.accountId, document.id));
    this.setLoadingForOcpDocument(document, false);
  };

  @action
  setLoadingForDocument = (document: Document, loading: boolean) => {
    this.data.reports = this.data.reports.map((doc) => {
      if (doc.type === document.type && doc.date === document.date) {
        doc.loading = loading;
      }
      return doc;
    });
  };

  @action
  setLoadingForOcpDocument = (document: Document, loading: boolean) => {
    this.data.summaries = this.data.summaries.map((doc) => {
      if (doc.type === document.type && doc.date === document.date) {
        doc.loading = loading;
      }
      return doc;
    });
  };

  clearTimer = () => {
    window.clearTimeout(this.timerID);
  };
}
